import { axiosRequest } from '@/plugins/axios-common'
import moment from 'moment-timezone'

import { AvailabilityRulesActions } from './partials/availability-rules'
import { AvailabilitySlotsActions } from './partials/availability-slots'
import { LinkedCalendarsActions } from './partials/linked-calendars'
import { PermissionActions } from './partials/permissions'
import { SchedulingAssistantActions } from './partials/scheduling-assistant'
import { handleError } from '@/store/handle-error'
import { sortByKeyCaseInsensitive } from '@/utilities'

function mapEventFromApi (event, timezone) {
  return {
    actionType: event.action_type,
    endDate: moment(event.end_date).tz(timezone).format(),
    id: event.id,
    isDone: event.is_done,
    legalCaseId: event.legal_case_id,
    link: event.link,
    name: event.name,
    recordHolderPartyName: event.record_holder_party_name,
    recordPublicId: event.record_public_id,
    startDate: moment(event.start_date).tz(timezone).format(),
    userId: event.user_id,
  }
}

function mapEventsFromApi (events, timezone) {
  return events.map(e => mapEventFromApi(e, timezone))
}

export const CalendarActions = {
  ...PermissionActions,
  ...AvailabilityRulesActions,
  ...AvailabilitySlotsActions,
  ...LinkedCalendarsActions,
  ...SchedulingAssistantActions,

  selectCalendarDate ({ commit, dispatch, state }, selectedDate) {
    const previousSelectedDate = state.selectedCalendar.date

    commit('setSelectedCalendarDate', selectedDate)

    if (!state.selectedCalendar.userId || !state.selectedCalendar.date) return dispatch('resetSelectedCalendar')

    if (!previousSelectedDate || !state.selectedCalendar.eventsCount.length) {
      dispatch('refreshSelectedCalendarEventsCount')
    }

    if (!previousSelectedDate || !state.selectedCalendar.events.length || (
      previousSelectedDate.getYear() !== selectedDate.getYear() ||
      previousSelectedDate.getMonth() !== selectedDate.getMonth()
    )) {
      dispatch('refreshSelectedCalendarEvents')
    }
  },

  selectCalendarTimezone ({ commit, dispatch, state }, timezone) {
    if (!timezone) return dispatch('resetSelectedCalendar')

    commit('setSelectedCalendarTimezone', timezone)

    if (!state.selectedCalendar.userId || !state.selectedCalendar.date) return dispatch('resetSelectedCalendar')

    dispatch('refreshSelectedCalendarEventsCount')
    dispatch('refreshSelectedCalendarEvents')
    dispatch('refreshAvailabilitySlots')
  },

  selectCalendarUser ({ commit, dispatch, state }, userId) {
    commit('setSelectedCalendarUserId', userId)

    if (!state.selectedCalendar.userId || !state.selectedCalendar.date) return dispatch('resetSelectedCalendar')

    dispatch('refreshSelectedCalendarEventsCount')
    dispatch('refreshSelectedCalendarEvents')
    dispatch('refreshAvailabilitySlots')
    dispatch('reloadPermissions')
  },

  resetSelectedCalendar ({ commit, dispatch }) {
    commit('setSelectedCalendarEventsCount', [])
    commit('setSelectedCalendarEvents', [])
    commit('updateAvailabilitySlots', [])
    dispatch('refreshLinkedCalendarsItems')
  },

  refreshSelectedCalendarEventsCount ({ commit, dispatch, state }) {
    if (!state.selectedCalendar.userId) return dispatch('resetSelectedCalendar')

    state.runningRequests++

    return axiosRequest.get(
      '/calendar/events_count',
      {
        params: {
          user_id: state.selectedCalendar.userId,
        },
      }
    ).
      then(({ data }) => {
        commit('setSelectedCalendarEventsCount', data)
        state.runningRequests--
      }).
      catch(handleError)
  },

  refreshSelectedCalendarEvents ({ commit, dispatch, state }) {
    if (!state.selectedCalendar.userId || !state.selectedCalendar.date) return dispatch('resetSelectedCalendar')

    state.runningRequests++

    dispatch('refreshAvailabilitySlots')
    dispatch('refreshLinkedCalendarsItems')

    const startDate = new Date(
      state.selectedCalendar.date.getFullYear(),
      state.selectedCalendar.date.getMonth(),
      1
    )

    const endDate = new Date(
      state.selectedCalendar.date.getFullYear(),
      state.selectedCalendar.date.getMonth() + 1,
      1
    )

    return axiosRequest.get(
      '/calendar/events',
      {
        params: {
          end_date: endDate,
          start_date: startDate,
          user_id: state.selectedCalendar.userId,
        },
      }
    ).
      then(({ data }) => {
        const events = mapEventsFromApi(data, state.selectedCalendar.timezone)
        commit('setSelectedCalendarEvents', events)
      }).
      catch(handleError).
      finally(_ => { state.runningRequests-- })
  },

  fetchAppointmentTypes ({ commit }) {
    return axiosRequest.get('/calendar/appointment_types').
      then(({ data }) => {
        commit('setAppointmentTypes', data)
      }).
      catch(handleError)
  },

  fetchCalendarEvents ({ state }, { startDate, endDate, userIds }) {
    if (state.abortControllers.fetchCalendarEvents) {
      state.abortControllers.fetchCalendarEvents.abort()
      state.abortControllers.fetchCalendarEvents = undefined
    }

    state.abortControllers.fetchCalendarEvents = new AbortController()

    return axiosRequest.get(
      '/calendar/events',
      {
        params: {
          end_date: endDate,
          start_date: startDate,
          user_id: userIds,
        },
        signal: state.abortControllers.fetchCalendarEvents.signal,
      }
    ).
      then(({ data }) => data).
      catch(handleError).
      finally(_ => { state.abortControllers.fetchCalendarEvents = undefined })
  },

  fetchTimezones ({ commit }) {
    return axiosRequest.get('/calendar/timezones').
      then(({ data }) => {
        commit('setTimezones', {
          main: data.main,
          other: sortByKeyCaseInsensitive(data.other, 'text'),
        })
      }).
      catch(handleError)
  },

  getCalendarItemDetails ({ state }, { calendarId, itemId, itemType, service }) {
    return axiosRequest.get('/calendar/item', {
      params: {
        calendar_id: calendarId,
        item_id: itemId,
        service,
        item_type: itemType,
        user_id: state.selectedCalendar.userId,
      },
    }).
      then(({ data }) => data).
      catch(handleError)
  },

  getCalendarGlanceItems: async ({ state }, { endDate, startDate }) => {
    if (state.abortControllers.getCalendarGlanceItems) {
      state.abortControllers.getCalendarGlanceItems.abort()
      state.abortControllers.getCalendarGlanceItems = null
    }

    state.abortControllers.getCalendarGlanceItems = new AbortController()

    return axiosRequest.get(
      '/calendar/glance',
      {
        params: {
          end_date: endDate,
          start_date: startDate,
        },
        signal: state.abortControllers.getCalendarGlanceItems.signal,
      }
    ).
      then(({ data }) => data).
      catch(handleError).
      finally(_ => { state.abortControllers.getCalendarGlanceItems = null })
  },
}
